import { useEffect, useRef } from "react";

import { shouldDropStoredSession } from "@/lib/authPersistence";
import { useAppDispatch, useAppSelector } from "@/store";
import { setAuthState } from "@/store/slices/authSlice";
import { setAuthCheckComplete } from "@/store/slices/commonSlice";
import {
  bootstrapStaffSession,
  setLogout,
  syncCustomerContextByPhone,
} from "@/store/thunks/auth";

/**
 * Decides who the visitor is, once, and then raises
 * `common.isAuthCheckComplete`.
 *
 * This is our stand-in for the original's `onAuthStateChanged` callback (the
 * `vt` hook in its `_app`). The ORDERING is the point, and it is reproduced
 * deliberately: the original installs its axios auth headers and only THEN
 * dispatches `setAuthCheckComplete(true)`, so that nothing authenticated can
 * fire before the session is settled. Consumers gate on the flag — the
 * recent-orders rail does, and the offers provider does.
 *
 * Two behaviours are faithful to the original and easy to mistake for bugs:
 *
 *  - The flag is only ever set to `true`, and only on the signed-in branch. A
 *    signed-out visitor leaves it `false` for the page's lifetime.
 *  - There is no loading UI. The rail simply does not fetch until the flag
 *    flips.
 *
 * Three things happen here that the original gets from Firebase instead:
 *
 *  1. **Session expiry.** A session created with "Remember Me" cleared is
 *     dropped when the browsing session that created it has ended — Firebase's
 *     SESSION persistence, ported in `lib/authPersistence.ts`.
 *  2. **`isLoggedIn` rehydration.** The whole `auth` slice is persisted (the
 *     original persists it too — it is first in its own whitelist), so the flag
 *     normally survives a reload on its own. It is re-raised here anyway
 *     because a session persisted by an EARLIER shape of this slice has no
 *     `isLoggedIn` key at all, and redux-persist's shallow merge would leave
 *     such a visitor with a customer record and a `false` flag — signed in by
 *     every appearance, signed out by every gate.
 *  3. **The refresh itself**, which is what keeps `order_ids` and the wallet
 *     balances from going stale between visits.
 */
export default function AuthBootstrap() {
  const dispatch = useAppDispatch();
  const customer = useAppSelector((state) => state.auth.customer);
  const phone = customer?.phone;

  // The expiry check must run before anything reads the session, and exactly
  // once — a later re-render must not sign a freshly logged-in customer out.
  const expiryChecked = useRef(false);

  useEffect(() => {
    if (expiryChecked.current) return;
    expiryChecked.current = true;
    if (phone && shouldDropStoredSession()) void dispatch(setLogout());
  }, [dispatch, phone]);

  /**
   * The staff half of the rehydration, once per load.
   *
   * `auth` is persisted, so a signed-in operator survives a reload on their
   * own — but the ROLE MATRIX deliberately is not, and a staff record with no
   * matrix has no permissions at all. This re-reads both, which also means a
   * role change, a platform change or a disablement lands on the next page load
   * rather than at the next sign-in.
   *
   * It is a no-op for everyone else: without a persisted `staff.uid` it returns
   * immediately, so a customer's boot is unchanged.
   */
  const staffChecked = useRef(false);
  useEffect(() => {
    if (staffChecked.current) return;
    staffChecked.current = true;
    void dispatch(bootstrapStaffSession());
  }, [dispatch]);

  useEffect(() => {
    if (!phone) return undefined;
    if (shouldDropStoredSession()) return undefined;
    let cancelled = false;
    // Re-raise the flag first: the persisted customer IS the session here, and
    // the gates downstream must not see a signed-in customer with
    // `isLoggedIn: false` for the duration of the round trip.
    dispatch(setAuthState({ isLoggedIn: true }));
    void dispatch(syncCustomerContextByPhone(phone)).finally(() => {
      // Set the flag whether or not the refresh succeeded — an offline
      // refresh must not strand the rail on its skeleton forever.
      if (!cancelled) dispatch(setAuthCheckComplete(true));
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch, phone]);

  return null;
}
